import { useCartPricing } from '../../contexts/CartPricingContext';
import { useLocale } from '../../contexts/LocaleContext';
import { hardwareDiscountPercentOff } from '../../lib/hardwareFirst';
import { centsToDollarsNumber } from '../../lib/util/money';

interface HardwareFirstTotals {
  /** Units across every repriced hardware item, for the "Hardware (n trackers)" label. */
  trackerCount: number;
  /** List price of the hardware before any discount. */
  hardwareInCents: number;
  /** Everything taken off the hardware - the arm's own repricing plus any coupon. */
  discountInCents: number;
  /** Pre-tax, matching the rows above it; the summary defers tax to checkout. */
  totalInCents: number;
  /** Whole percent off, for the pinned bar's pill. Zero when nothing was taken off. */
  percentOff: number;
  formatPrice: (cents: number) => string;
}

/**
 * The hardware-first cart's receipt figures. The discount is derived from the hardware list price
 * and the server total rather than summed from the individual discount sources, so the column the
 * shopper reads always adds up even when a coupon stacks on top of the arm's repricing.
 *
 * Everything here is pre-tax. The summary lists hardware, discount and shipping and then says taxes
 * are calculated at checkout, so a tax-inclusive total would not equal the rows above it - the
 * server includes tax in `totalInCents` whenever it priced the cart with it.
 */
export default function useHardwareFirstTotals(): HardwareFirstTotals {
  const cartPricing = useCartPricing();
  const { currencyFormatter } = useLocale();
  const { hardwareDiscounts, totalInCents, shippingInCents, taxInCents } = cartPricing;

  const discounts = Object.values(hardwareDiscounts ?? {});
  const trackerCount = discounts.length;
  const hardwareInCents = discounts.reduce((sum, discount) => sum + discount.listPriceInCents, 0);
  const preTaxTotalInCents = Math.max(totalInCents - (taxInCents ?? 0), 0);
  const chargesInCents = hardwareInCents + (shippingInCents ?? 0);
  const discountInCents = Math.max(chargesInCents - preTaxTotalInCents, 0);

  return {
    trackerCount,
    hardwareInCents,
    discountInCents,
    totalInCents: preTaxTotalInCents,
    percentOff: hardwareDiscountPercentOff(hardwareInCents, hardwareInCents - discountInCents),
    formatPrice: (cents: number) => currencyFormatter.format(centsToDollarsNumber(cents)).replace(/\.00$/, ''),
  };
}
